import React, { useMemo } from 'react';
import { Button } from '@mui/material';
import { DeleteForever } from '@mui/icons-material';
import { hideLoader, showLoader } from '@utils/Loader';
import { showAlertError } from '@utils/Alerts/Error';
import { showAlertMessage } from '@utils/Alerts/Message';
import { showIOSAlert } from '@utils/Alerts/Confirm';
import { currencyFormatter } from '@utils/Numbers';
import OrdersService from '@api/Orders/OrderCtrl';
import { isTableOrder, Order, OrderStatus } from '@api/Orders/Order';
import { PaymentMethod, getOrderPaidBy } from '@api/Orders/OrderPayments';

interface OrderPurgeButtonProps {
    order?: Order;
    /**
     * El aviso largo: el total del pedido en la confirmación y, al terminar, qué
     * se deshizo. Lo usa `ModalViewOrder` (donde se investiga un pedido); la
     * lista de `/orders` da el corto. Se conservan los dos textos tal cual.
     */
    detailed?: boolean;
    /** Tras borrar: la lista suelta la selección, el modal se cierra. */
    onPurged: () => void;
}

/**
 * El botón "Eliminar" de un pedido, compartido por el detalle de `/orders` y
 * `ModalViewOrder`.
 *
 * Borra el pedido y DESHACE sus efectos (caja, mesa, puntos) — pensado para
 * depurar pedidos de prueba. Es un hard-delete: no hay papelera ni deshacer. Un
 * tablero realtime se actualiza solo. **Quién puede** lo decide el servicio
 * (`purgeOrder` exige `OrdersPurge`); cada pantalla decide solo si pinta el botón.
 */
const OrderPurgeButton: React.FunctionComponent<OrderPurgeButtonProps> = ({ order, detailed = false, onPurged }) => {
    const ordersService = useMemo(() => new OrdersService(), []);

    const onConfirmDeleteOrder = () => {
        if (!order?.id) return;
        const affectsCash = getOrderPaidBy(order, PaymentMethod.Cash) > 0 && order.status === OrderStatus.Completed;
        const affectsPoints = !!order.customer?.uid;
        const pointsLine = detailed
            ? 'Se recalcularán los puntos del cliente (pierde los de este pedido)'
            : 'Se recalcularán los puntos del cliente';
        const consequences = [
            'Se borrará el pedido y su historial de actividad',
            affectsCash ? 'Se quitará su ingreso en efectivo del corte de caja' : '',
            affectsPoints ? pointsLine : '',
            isTableOrder(order) ? 'Se liberará la mesa si no le quedan pedidos activos' : ''
        ].filter((line) => line !== '');
        const list = consequences.map((line) => `• ${line}`).join('\n');
        showIOSAlert({
            title: `Eliminar pedido #${order.shortId}`,
            message: detailed
                ? `Esta acción NO se puede deshacer.\n\n${list}\n\nTotal del pedido: ${currencyFormatter(order.total)}`
                : `Esta acción NO se puede deshacer.\n\n${list}`,
            buttons: [
                { text: 'Cancelar', style: 'cancel', onPress: () => {} },
                {
                    text: 'Eliminar',
                    style: 'destructive',
                    onPress: async () => {
                        showLoader();
                        try {
                            const result = await ordersService.purgeOrder(order.id as string);
                            if (detailed) {
                                const extras = [
                                    result.cashFlowsDeleted > 0 ? 'movimiento de caja eliminado' : '',
                                    result.pointsRebuilt ? 'puntos del cliente recalculados' : '',
                                    result.tableReleased ? 'mesa liberada' : ''
                                ].filter((detail) => detail !== '');
                                showAlertMessage(
                                    `Pedido eliminado${extras.length > 0 ? ` (${extras.join(', ')})` : ''}`
                                );
                                onPurged();
                            } else {
                                onPurged();
                                showAlertMessage('Pedido eliminado');
                            }
                        } catch (error: any) {
                            showAlertError(error);
                        }
                        hideLoader();
                    }
                }
            ]
        });
    };

    return (
        <Button variant="outlined" color="error" startIcon={<DeleteForever />} onClick={onConfirmDeleteOrder}>
            Eliminar
        </Button>
    );
};

export default OrderPurgeButton;
